import { Timestamp } from '@firebase/firestore';

import { fetchItem } from '../api/generic';
import { FermentationStatus } from '../utils/hydroPi';
import { prefixCollection } from '../utils/prefix';
import { slugify } from '../utils/string';
import { Recipe } from './Recipe';
import { TasteSample } from './Sampling';

export type BatchStatus = 'planned' | 'in-progress' | 'completed' | 'archived';
export type StepStaus = 'not-started' | 'in-progress' | 'completed';
export const HydroPiColors = ['Black', 'Yellow', 'Red', 'Blue', 'Green'] as const;
export type HydroPiColor = (typeof HydroPiColors)[number];

export type Batch = {
  id: string; // Unique identifier for the batch
  name: string; // Name of the batch
  batchId?: string; // YEAR_INCREMENT
  recipeId: string; // ID of the recipe used
  recipe?: Recipe; // Recipe used
  startDate: Timestamp | Date; // Date when the batch started
  endDate?: Timestamp | Date; // Date when the batch ended (optional)
  status: BatchStatus; // Status of the
  quantity: number; // Quantity produced in this batch
  volume: number; // Starting volume produced in this batch
  yield: number; // Yield of the batch
  expectedYield: number; // Expected yield of the batch
  currentStepIndex: number; // Index of the current step in the recipe's steps
  stepStatus: StepStaus; // Status of the current
  notes?: string; // Optional notes about the batch
  score?: number;
  hydroPi?: HydroPiConfig;
  tasteSamples?: TasteSample[];
  exciseDuty?: ExciseInfo;
  locked?: boolean; // Prevent edits after batch is finalized
  createdBy?: string; // User UID
  lastModifiedBy?: string; // Optional, for audit trail
  lastModifiedAt?: Timestamp | Date;
  abvFinal?: number;
  department?: string;
  ingredientsConfirmed?: boolean;
  recipeSlug?: string;
};

export const getBatchSlug = async (batch: Batch, tenant: string) => {
  const slug = batch.recipeSlug ?? batch.recipe?.slug;
  if (slug) {
    return slug;
  }
  const recipe = await fetchItem<Recipe>(prefixCollection('recipes', tenant), batch.recipeId);
  return recipe?.slug ?? slugify(recipe?.name?.split(' ')[0] ?? '');
};

export type ExciseInfo = {
  exciseDutyPaid?: Timestamp | Date; // Indicates if the excise duty has been paid
  exciseDutyAmount?: number; // Amount of excise duty paid
  exciseDutyPaymentReferenceId?: string; // Reference ID for the payment
};

export type HydroPiConfig = {
  color: HydroPiColor;
  startDate: Timestamp;
  endDate?: Timestamp;
  type?: 'iSpindel' | 'Tilt';
};

export interface BatchViewModel extends Batch {
  abvNow: string; // Current ABV based on the latest sample
  currentStepName: string; // Current step name from the recipe
  progress: number; // Progress percentage calculated
  costL: string; // Cost per liter calculated to two decimal places
  nextActionDate: string;
  fStatus?: FermentationStatus;
  finalAbv: string;
  slug?: string;
}
